import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Trash2, Loader2, Copy, Download } from "lucide-react";
import { api } from "@/api/client";
import { downloadBlob } from "@/lib/download";
import { createPoller, type Poller } from "@/lib/poller";
import type { StudyDetail, StudyRunModelRun, StudyRunStanding } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ErrorState } from "@/components/ui/error-state";
import { formatModelLabel, formatModelSummary } from "@/lib/format";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { SchedulePanel } from "./SchedulePanel";
import { ConsistencyPanel } from "./ConsistencyPanel";

const studyTypeLabels: Record<string, string> = {
  tournament: "Tournament",
  scoring: "Scoring",
  sentiment: "Sentiment",
  blind_query: "Blind Query",
};

type LoadState = "loading" | "ready" | "not-found" | "error";

interface StudyDetailViewProps {
  studyId: number;
  onBack: () => void;
}

interface ModelRunStatusSummaryProps {
  modelRuns: StudyRunModelRun[];
}

/**
 * Renders compact per-model statuses for a multi-model study run.
 *
 * @param props Model run status summary props.
 * @return Per-model status summary.
 */
function ModelRunStatusSummary({ modelRuns }: ModelRunStatusSummaryProps) {
  if (modelRuns.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {modelRuns.map((modelRun) => (
        <div key={modelRun.id} className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-semibold text-brand-950">{formatModelLabel(modelRun.model)}</span>
            <StatusBadge status={modelRun.status} />
          </div>
          {modelRun.failure_reason && (
            <p className="mt-1 max-w-xs text-[11px] leading-snug text-slate-500">
              {modelRun.failure_reason}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// Simple color palette for chart lines
const BRAND_COLORS = [
  "#4f46e5", "#f59e0b", "#16a34a", "#dc2626", "#8b5cf6",
  "#0891b2", "#d946ef", "#ea580c", "#64748b", "#059669",
];

export function StudyDetailView({ studyId, onBack }: StudyDetailViewProps) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<StudyDetail | null>(null);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [running, setRunning] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const pollerRef = useRef<Poller | null>(null);

  const fetchDetail = async () => {
    try {
      const data = await api.studies.get(studyId);
      setDetail(data);
      setLoadState("ready");
      setError("");
    } catch (err: any) {
      const message = err?.message || "Unable to load study";
      setError(message);
      setLoadState(message.toLowerCase().includes("not found") ? "not-found" : "error");
    }
  };

  useEffect(() => {
    setLoadState("loading");
    fetchDetail();
    // Cancel any in-flight poll on unmount / study change so it can't fire
    // setState after the component is gone.
    return () => { pollerRef.current?.cancel(); pollerRef.current = null; };
  }, [studyId]);

  const handleRun = async () => {
    if (!detail) return;
    setRunning(true);
    setActionError("");

    try {
      const { run_id } = await api.studies.run(studyId);

      pollerRef.current?.cancel();
      pollerRef.current = createPoller(
        async () => {
          const run = await api.studies.getRun(run_id);
          if (run.status === "completed" || run.status === "failed") {
            setRunning(false);
            fetchDetail();
            return true;
          }
          return false;
        },
        {
          intervalMs: 3000,
          timeoutMs: 600000,
          onTimeout: () => { setRunning(false); fetchDetail(); },
        },
      );

      // Refresh immediately to show the running state
      fetchDetail();
    } catch (err: any) {
      setRunning(false);
      setActionError(err?.message || "Failed to start study run");
    }
  };

  /**
   * Duplicates the current study config and opens the copied study.
   *
   * @return Promise that resolves after the duplicate request finishes.
   */
  const handleDuplicate = async (): Promise<void> => {
    if (!detail) return;

    setDuplicating(true);
    setActionError("");

    try {
      const copy = await api.studies.duplicate(studyId, {});
      navigate(`/app/studies/${copy.id}`);
    } catch (err: any) {
      setActionError(err?.message || "Failed to duplicate study");
    } finally {
      setDuplicating(false);
    }
  };

  /**
   * Downloads the study's run history as a workspace-scoped CSV.
   *
   * @return Promise that resolves after the download is triggered.
   */
  const handleExport = async (): Promise<void> => {
    if (!detail) return;

    setExporting(true);
    setActionError("");

    try {
      const csvBlob = await api.studies.exportCsv(studyId);
      const safeName = detail.study.name.replace(/[^a-z0-9\-_]+/gi, "_").slice(0, 60) || "study";
      downloadBlob(csvBlob, `${safeName}.csv`);
    } catch (err: any) {
      setActionError(err?.message || "Failed to export study");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Delete this study and all its runs?")) return;
    await api.studies.delete(studyId);
    onBack();
  };

  if (loadState === "loading") return <Spinner message="Loading study..." size="lg" />;
  if (loadState === "not-found") return <p className="text-sm text-slate-500">Study not found.</p>;
  if (loadState === "error" || !detail) {
    return <ErrorState message={error} onRetry={() => { setLoadState("loading"); fetchDetail(); }} />;
  }

  const { study, prompt_name, brand_names, runs, latest_standings, total_runs } = detail;
  const completedRuns = runs.filter(r => r.status === "completed");
  const brandIds = study.brand_ids;
  const studyModels = study.models?.length ? study.models : [study.model];
  const brandSummary = study.tracked_brand_source === "workspace_all" && brandIds.length === 0
    ? "All workspace brands"
    : `${brandIds.length} brand${brandIds.length === 1 ? "" : "s"}`;
  const latestRun = runs[runs.length - 1];
  const latestModelRuns = latestRun?.model_runs ?? [];
  const chartBrandIds = brandIds.length > 0 ? brandIds : latest_standings?.map(standing => standing.brand_id) ?? [];
  const chartBrandNames = { ...brand_names };
  latest_standings?.forEach((standing) => {
    chartBrandNames[standing.brand_id] = chartBrandNames[standing.brand_id] || standing.brand_name;
  });

  return (
    <div className="space-y-6">
      {/* Back + Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-brand-950 transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          All Studies
        </button>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleExport} disabled={exporting || total_runs === 0}>
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
          <Button variant="secondary" onClick={handleDuplicate} disabled={duplicating}>
            {duplicating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
            {duplicating ? "Duplicating..." : "Duplicate"}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-red-600"
            onClick={handleDelete}
            aria-label="Delete study"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button onClick={handleRun} disabled={running}>
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            {running ? "Running..." : "Run Now"}
          </Button>
        </div>
      </div>

      {actionError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3" role="alert">
          <p className="text-[13px] text-red-700">{actionError}</p>
        </div>
      )}

      {/* Study Info */}
      <Card>
        <CardContent className="py-5 px-6">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-lg font-bold text-brand-950">{study.name}</h2>
            <Badge variant={study.type === "blind_query" ? "default" : "muted"}>{studyTypeLabels[study.type] || study.type}</Badge>
          </div>
          <div className="flex gap-4 text-[12px] text-slate-400">
            <span>{prompt_name}</span>
            <span>{formatModelSummary(studyModels)}</span>
            <span>{brandSummary}</span>
            <span>{total_runs} run{total_runs !== 1 ? "s" : ""}</span>
          </div>
          {study.type === "blind_query" && (
            <p className="mt-3 text-[12px] text-slate-500 leading-relaxed">
              Blind Query study: tracked brands are monitored after each model response and are not included in the prompt.
            </p>
          )}
        </CardContent>
      </Card>

      {latestModelRuns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Latest per-model status</CardTitle>
            <CardDescription>Child runs for the most recent combined study run.</CardDescription>
          </CardHeader>
          <CardContent>
            <ModelRunStatusSummary modelRuns={latestModelRuns} />
          </CardContent>
        </Card>
      )}

      {/* Recurring schedule */}
      <SchedulePanel studyId={studyId} />

      {/* Current Standings */}
      {latest_standings && latest_standings.length > 0 && (
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>Current Standings</CardTitle>
            <CardDescription>From the most recent run</CardDescription>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead className="text-right">Score</TableHead>
                {study.type === "tournament" && (
                  <>
                    <TableHead className="text-right">W</TableHead>
                    <TableHead className="text-right">L</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {latest_standings.map((s, i) => (
                <TableRow key={s.brand_id}>
                  <TableCell className="font-semibold text-slate-400 tabular-nums">{s.rank}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND_COLORS[i % BRAND_COLORS.length] }} />
                      <span className="font-medium text-brand-950">{s.brand_name}</span>
                      {i === 0 && <Badge variant="winner">Leader</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-semibold text-brand-950 tabular-nums">{s.score}</TableCell>
                  {study.type === "tournament" && (
                    <>
                      <TableCell className="text-right font-semibold text-green-600 tabular-nums">{s.wins ?? 0}</TableCell>
                      <TableCell className="text-right font-semibold text-red-500 tabular-nums">{s.losses ?? 0}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Rank Trajectory Chart */}
      {completedRuns.length >= 2 && chartBrandIds.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Rank Over Time</CardTitle>
            <CardDescription>Position changes across runs (1 = best)</CardDescription>
          </CardHeader>
          <CardContent>
            <RankChart runs={completedRuns} brandIds={chartBrandIds} brandNames={chartBrandNames} />
          </CardContent>
        </Card>
      )}

      {/* Score Trajectory Chart */}
      {completedRuns.length >= 2 && chartBrandIds.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Score Over Time</CardTitle>
            <CardDescription>Average score trajectory across runs</CardDescription>
          </CardHeader>
          <CardContent>
            <ScoreChart runs={completedRuns} brandIds={chartBrandIds} brandNames={chartBrandNames} />
          </CardContent>
        </Card>
      )}

      {/* Answer consistency between consecutive runs */}
      <ConsistencyPanel studyId={studyId} runs={runs} refreshKey={total_runs} />

      {/* Run History */}
      {runs.length > 0 && (
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>Run History</CardTitle>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Winner</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...runs].reverse().map(run => (
                <TableRow key={run.id}>
                  <TableCell className="font-medium text-brand-950 tabular-nums">#{run.run_number}</TableCell>
                  <TableCell className="text-slate-500 text-[13px]">{run.run_date}</TableCell>
                  <TableCell>
                    {run.winner ? (
                      <span className="font-medium text-brand-950">{run.winner}</span>
                    ) : (
                      <span className="text-slate-300">--</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={run.status} />
                    {run.status === "failed" && run.failure_reason && (
                      <p className="mt-1 max-w-sm text-[11px] leading-snug text-slate-500">
                        {run.failure_reason}
                      </p>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {total_runs === 0 && !running && (
        <div className="text-center py-12">
          <p className="text-sm text-slate-500 mb-3">No runs yet. Click "Run Now" to start tracking.</p>
        </div>
      )}
    </div>
  );
}

// Chart geometry shared by both trajectory charts.
const CHART_WIDTH = 600;
const CHART_HEIGHT = 200;
const CHART_PAD_L = 40;
const CHART_PAD_R = 20;
const CHART_PAD_T = 10;
const CHART_PAD_B = 30;
const CHART_W = CHART_WIDTH - CHART_PAD_L - CHART_PAD_R;
const CHART_H = CHART_HEIGHT - CHART_PAD_T - CHART_PAD_B;

interface YGridLine {
  /** Value plotted on the y axis (used as a React key and as the tick label). */
  value: number;
  /** Rendered tick label (defaults to value). */
  label: string;
}

// SVG-based trajectory chart (no charting library needed). Shared by the rank and
// score charts, which differ only in their y-axis scale and the per-standing value.
function TrajectoryChart({
  runs,
  brandIds,
  brandNames,
  getValue,
  getY,
  yGridLines,
}: {
  runs: { run_number: number; standings: StudyRunStanding[] }[];
  brandIds: number[];
  brandNames: Record<number, string>;
  /** Map a standing (or its absence) to the plotted numeric value. */
  getValue: (standing: StudyRunStanding | undefined) => number;
  /** Map a plotted value to its y pixel coordinate. */
  getY: (value: number) => number;
  /** Y-axis tick lines (label + gridline). */
  yGridLines: YGridLine[];
}) {
  const xStep = runs.length > 1 ? CHART_W / (runs.length - 1) : 0;

  return (
    <div>
      <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} className="w-full h-auto">
        {/* Y axis (labels + grid lines) */}
        {yGridLines.map(({ value, label }) => (
          <g key={value}>
            <text x={CHART_PAD_L - 8} y={getY(value) + 4} textAnchor="end" className="fill-slate-400 text-[10px]">{label}</text>
            <line x1={CHART_PAD_L} y1={getY(value)} x2={CHART_WIDTH - CHART_PAD_R} y2={getY(value)} stroke="#f1f5f9" strokeWidth={1} />
          </g>
        ))}

        {/* X axis labels */}
        {runs.map((run, i) => (
          <text key={i} x={CHART_PAD_L + i * xStep} y={CHART_HEIGHT - 6} textAnchor="middle" className="fill-slate-400 text-[10px]">
            #{run.run_number}
          </text>
        ))}

        {/* Lines per brand */}
        {brandIds.map((brandId, bIdx) => {
          const points = runs.map((run, i) => {
            const standing = run.standings.find(s => s.brand_id === brandId);
            return { x: CHART_PAD_L + i * xStep, y: getY(getValue(standing)) };
          });

          const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
          const color = BRAND_COLORS[bIdx % BRAND_COLORS.length];

          return (
            <g key={brandId}>
              <path d={pathD} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              {points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r={3} fill={color} />
              ))}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-3">
        {brandIds.map((id, i) => (
          <div key={id} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: BRAND_COLORS[i % BRAND_COLORS.length] }} />
            <span className="text-[11px] font-medium text-slate-600">{brandNames[id]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

type ChartProps = {
  runs: { run_number: number; standings: StudyRunStanding[] }[];
  brandIds: number[];
  brandNames: Record<number, string>;
};

// Rank trajectory: 1 = best at top, evenly spaced down to the worst rank.
function RankChart({ runs, brandIds, brandNames }: ChartProps) {
  const totalBrands = brandIds.length;
  const getY = (rank: number) => CHART_PAD_T + ((rank - 1) / Math.max(1, totalBrands - 1)) * CHART_H;
  const yGridLines: YGridLine[] = Array.from({ length: totalBrands }, (_, i) => i + 1).map(rank => ({
    value: rank,
    label: `${rank}${rank === 1 ? "st" : rank === 2 ? "nd" : rank === 3 ? "rd" : "th"}`,
  }));

  return (
    <TrajectoryChart
      runs={runs}
      brandIds={brandIds}
      brandNames={brandNames}
      getValue={(standing) => standing?.rank ?? totalBrands}
      getY={getY}
      yGridLines={yGridLines}
    />
  );
}

// Score trajectory: average score plotted against an auto-scaled y axis.
function ScoreChart({ runs, brandIds, brandNames }: ChartProps) {
  // Find score range
  let minScore = 100, maxScore = 0;
  for (const run of runs) {
    for (const s of run.standings) {
      if (s.score < minScore) minScore = s.score;
      if (s.score > maxScore) maxScore = s.score;
    }
  }
  const floor = Math.max(0, minScore - 5);
  const ceil = Math.min(100, maxScore + 5);
  const scaleRange = ceil - floor;

  const getY = (score: number) => CHART_PAD_T + ((ceil - score) / scaleRange) * CHART_H;
  const yGridLines: YGridLine[] = [floor, Math.round((floor + ceil) / 2), ceil].map(value => ({ value, label: String(value) }));

  return (
    <TrajectoryChart
      runs={runs}
      brandIds={brandIds}
      brandNames={brandNames}
      getValue={(standing) => standing?.score ?? 0}
      getY={getY}
      yGridLines={yGridLines}
    />
  );
}
